import type { ChatwootAttributes } from '@fordeer/chatwoot-widget'
import type { InvoiceShopInfo } from './chatwoot'

export const SUPPORT_PROVIDERS = {
  CHATWOOT: 'chatwoot',
} as const

export type SupportProvider =
  (typeof SUPPORT_PROVIDERS)[keyof typeof SUPPORT_PROVIDERS]

export interface SupportContext {
  source: string
  topic: string
  message?: string
}

export interface SupportOperations {
  initializeChatwoot(
    shop: InvoiceShopInfo,
    additionalBenefits?: string,
  ): Promise<boolean>
  hideChatwoot(): Promise<boolean>
  showChatwoot(): Promise<boolean>
  openChatwoot(attributes: ChatwootAttributes): Promise<boolean>
  popoutChatwoot(attributes: ChatwootAttributes): Promise<boolean>
}

const getConversationAttributes = ({
  source,
  topic,
  message,
}: SupportContext) => ({
  support_source: source,
  support_topic: topic,
  support_message: message || '',
})

export const createSupportCoordinator = (operations: SupportOperations) => {
  let activeProvider: SupportProvider | null = null
  let initialization: Promise<boolean> | null = null
  let initializationSequence = 0

  const initializeSupport = (
    shop: InvoiceShopInfo,
    additionalBenefits?: string,
  ): Promise<boolean> => {
    if (initialization) {
      return initialization
    }

    const sequence = ++initializationSequence
    initialization = (async () => {
      const initialized = await operations.initializeChatwoot(
        shop,
        additionalBenefits,
      )

      if (sequence !== initializationSequence) {
        return initialized
      }

      if (!initialized) return false

      activeProvider = SUPPORT_PROVIDERS.CHATWOOT
      return true
    })().finally(() => {
      if (sequence === initializationSequence) {
        initialization = null
      }
    })

    return initialization
  }

  const openSupport = async (context: SupportContext): Promise<boolean> => {
    if (!activeProvider && !initialization) {
      console.warn('[Support] Support has not started initializing', context)
      return false
    }

    if (initialization) {
      await initialization
    }

    if (!activeProvider) {
      console.warn('[Support] Failed to open support', {
        ...context,
        provider: activeProvider,
      })
      return false
    }

    const opened = await operations.openChatwoot(
      getConversationAttributes(context),
    )

    if (!opened) {
      console.warn('[Support] Failed to open support', {
        ...context,
        provider: activeProvider,
      })
    }
    return opened
  }

  const hideSupport = (): Promise<boolean> => operations.hideChatwoot()

  const showSupport = (): Promise<boolean> => operations.showChatwoot()

  const openSupportInNewTab = (context: SupportContext): void => {
    const attributes = getConversationAttributes(context)
    if (!activeProvider && initialization) {
      void initialization.then(success => {
        if (success) {
          void operations.popoutChatwoot(attributes)
        }
      })
      return
    }

    void operations.popoutChatwoot(attributes)
  }

  return {
    initializeSupport,
    openSupport,
    openSupportInNewTab,
    hideSupport,
    showSupport,
  }
}
